import { useEffect, useId, useMemo, useState } from "react";
import { useHudStore } from "../../store/hudStore";
import { Button, Card, Field, Select, Stat, Tabs } from "../../design-system/components";
import { buildFinanceAnalytics, type FinanceTransaction } from "../../lib/financeAnalytics";
import { refreshFinances } from "../../lib/live";
import { CategoryDonut, MoneyTrend, BalanceBars } from "./FinanceCharts";
import "./FinanceAnalytics.css";

const money = (amount: number) => new Intl.NumberFormat("en-PH", { style: "currency", currency: "PHP", minimumFractionDigits: 2 }).format(amount);
const PAGE_SIZE = 12;
const CATEGORY_COLORS: Record<string, string> = {
  food: "#f2799e", medical: "#faaec4", transport: "#49b8ea", bills: "#a9c9ff",
  home: "#ff9166", support: "#ffba96", travel: "#9ed85c", subscription: "#6ea8ff",
  game: "#4a86ec", fun: "#8cb7f0", other: "#839acd",
};
const KIND_LABELS = { income: "Income", expense: "Spending", transfer: "Transfer", "debt-payment": "Debt payment" };

function csvCell(value: string) {
  // A downloaded ledger should stay text when opened in a spreadsheet.
  const safe = /^[=+@\-\t\r]/.test(value) ? "'" + value : value;
  return '"' + safe.replace(/"/g, '""') + '"';
}

function transactionAccount(row: FinanceTransaction) {
  return row.kind === "transfer" ? `${row.fromAccountName} → ${row.toAccountName}` : row.accountName;
}

export default function FinanceAnalytics() {
  const st = useHudStore();
  const nowIso = new Date(Date.now() + 8 * 3600 * 1000).toISOString().slice(0, 10);
  const [month, setMonth] = useState(() => {
    const saved = new URLSearchParams(location.search).get("month") || "";
    return saved === "all" || /^\d{4}-(0[1-9]|1[0-2])$/.test(saved) ? saved : nowIso.slice(0, 7);
  });
  const [account, setAccount] = useState(() => new URLSearchParams(location.search).get("account") || "");
  const [category, setCategory] = useState<string | null>(null);
  const [kind, setKind] = useState("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("");
  const tableId = `finance-transactions-${useId()}`;

  const data = useMemo(() => buildFinanceAnalytics({
    accounts: st.accounts, incomes: st.incomes, expenses: st.expenses, transfers: st.transfers,
    payments: st.payments, debts: st.debts,
  }, { month, account, nowIso, ownerName: "Arlou" }), [st.accounts, st.incomes, st.expenses, st.transfers, st.payments, st.debts, month, account, nowIso]);

  useEffect(() => {
    const url = new URL(location.href);
    url.searchParams.set("view", "charts");
    url.searchParams.set("month", month);
    if (account) url.searchParams.set("account", account); else url.searchParams.delete("account");
    history.replaceState(null, "", url.pathname + url.search);
  }, [month, account]);

  const accountOptions = useMemo(() => {
    const options = new Map(data.accounts.map((a) => [a.id, a.name]));
    for (const row of data.allTransactions) {
      if (row.account) options.set(row.account, row.accountName);
      if (row.fromAccount) options.set(row.fromAccount, row.fromAccountName || row.fromAccount);
      if (row.toAccount) options.set(row.toAccount, row.toAccountName || row.toAccount);
    }
    return [...options].sort((a, b) => a[1].localeCompare(b[1]));
  }, [data.accounts, data.allTransactions]);

  const rows = data.transactions.filter((row) =>
    (kind === "all" || row.kind === kind) &&
    (!category || row.kind === "expense" && row.category === category) &&
    (!search.trim() || `${row.label} ${row.categoryLabel} ${transactionAccount(row)} ${row.date} ${row.iso} ${row.amount}`.toLowerCase().includes(search.trim().toLowerCase())));
  const pages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const safePage = Math.min(page, pages - 1);
  const visibleRows = rows.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE);
  const period = month === "all" ? "All recorded months" : data.months.find((m) => m.value === month)?.label || month;
  const largest = data.categories[0];
  const trend = month === nowIso.slice(0, 7)
    ? data.trend.slice(0, Math.max(Number(nowIso.slice(8, 10)), ...data.trend.map((point, index) => point.income || point.spending || point.debtPayments ? index + 1 : 0)))
    : data.trend;
  const spendingByAccount = new Map<string, { id: string; label: string; cents: number }>();
  for (const row of data.transactions) if (row.kind === "expense") {
    const item = spendingByAccount.get(row.account) || { id: row.account || "unrecorded", label: row.accountName, cents: 0 };
    item.cents += row.cents;
    spendingByAccount.set(row.account, item);
  }
  const accountSpending = [...spendingByAccount.values()].map((item) => ({ id: item.id, label: item.label, amount: item.cents / 100 })).sort((a, b) => b.amount - a.amount);

  function chooseCategory(key: string | null) {
    setCategory(key);
    setKind(key ? "expense" : "all");
    setPage(0);
    if (key) requestAnimationFrame(() => document.getElementById(tableId)?.closest(".ds-card")?.scrollIntoView({ block: "start", behavior: "auto" }));
  }

  async function refresh() {
    setRefreshing(true); setError(""); setStatus("");
    try {
      const ok = await refreshFinances();
      if (!ok) setError("Your finances could not refresh. Check your connection and try again.");
      else setStatus("Finances refreshed.");
    } catch {
      setError("Your finances could not refresh. Check your connection and try again.");
    } finally { setRefreshing(false); }
  }

  function exportCsv() {
    const lines = ["Date,Type,Transaction,Category,Account,Amount (PHP)", ...rows.map((row) => [
      csvCell(row.iso || row.date || "Date not recorded"), csvCell(KIND_LABELS[row.kind]), csvCell(row.label),
      csvCell(row.categoryLabel), csvCell(transactionAccount(row)), row.amount.toFixed(2),
    ].join(","))];
    const url = URL.createObjectURL(new Blob(["\uFEFF" + lines.join("\r\n")], { type: "text/csv;charset=utf-8" }));
    const link = document.createElement("a");
    link.href = url; link.download = `life-os-transactions-${month}.csv`; link.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    setStatus(`Exported ${rows.length} transactions.`);
  }

  return <div className="finance-analytics" aria-busy={refreshing}>
    <Card title="Your money at a glance" description="See where your money goes, then explore the transactions behind it." actions={<Button icon="fa-solid fa-arrows-rotate" loading={refreshing} onClick={refresh}>Refresh</Button>}>
      <div className="fa-period-controls">
        <Select label="Period" value={month} onChange={(event) => { setMonth(event.target.value); chooseCategory(null); setPage(0); }}>
          {data.months.filter((m) => m.value !== "all").map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
          <option value="all">All recorded months</option>
        </Select>
        <Select label="Account" value={account} onChange={(event) => { setAccount(event.target.value); chooseCategory(null); setPage(0); }}>
          <option value="">All accounts</option>
          {accountOptions.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
        </Select>
        <p className="fa-period-note">{period}<br />{data.summary.transactionCount} recorded transactions</p>
      </div>
      {error && <p role="alert" className="fa-feedback fa-feedback--error">{error}</p>}
      {status && <p role="status" className="fa-feedback">{status}</p>}
    </Card>

    <div className="fa-summary-grid">
      <Card><Stat label="Income received" value={money(data.summary.income)} tone="accent" /><p className="fa-note">Recorded income in this period</p></Card>
      <Card><Stat label="Spent" value={money(data.summary.spending)} /><p className="fa-note">Purchases, bills, and fees</p></Card>
      <Card><Stat label="Debt payments" value={money(data.summary.debtPayments)} /><p className="fa-note">Repayments shown separately</p></Card>
      <Card><Stat label="Funds now" value={money(data.availableFunds)} tone="accent" /><p className="fa-note">All cash, savings, and wallets</p></Card>
    </div>

    <div className="fa-chart-grid">
      <Card title="Spending by category" description="Select a category to see its transactions below." className="fa-chart-card">
        <CategoryDonut items={data.categories.map((item, index) => ({ ...item, color: CATEGORY_COLORS[item.key] || ["#a9c9ff", "#839acd", "#8cb7f0", "#aac6f1"][index % 4]! }))} total={data.summary.spending} selected={category} onSelect={chooseCategory} />
        {largest && <p className="fa-insight"><strong>{largest.label}</strong> is your largest category at <strong>{money(largest.amount)}</strong>, {largest.percent.toFixed(1)}% of spending.</p>}
      </Card>
      <Card title={month === "all" ? "Income and spending by month" : "Daily income and spending"} description={`${period}. Income, spending, and debt repayments shown separately.`} className="fa-chart-card">
        <MoneyTrend items={trend.map((point) => ({ label: point.label, income: point.income, expense: point.spending, debt: point.debtPayments }))} />
        <p className="fa-note">Transfers move money between accounts. They do not count as income or spending here.</p>
      </Card>
      <Card title="Where you spent" description={`${period}. Purchases and bills by payment account.`} className="fa-chart-card">
        <BalanceBars items={accountSpending} label="Spending by payment account" note="Bar lengths compare spending through each payment account." />
      </Card>
      <Card title="Account balances now" description="Current cash, savings, and wallets, across all accounts." className="fa-chart-card">
        <BalanceBars items={data.cashAccounts.map((item) => ({ id: item.id, label: item.name, amount: item.balance }))} />
        <p className="fa-note">Credit balances are tracked with debt. These balances are current, even when you select a past month.</p>
      </Card>
    </div>

    <Card title="Transactions" description="Every recorded income, expense, transfer, and debt payment in the selected period." actions={<Button icon="fa-solid fa-file-arrow-down" disabled={!rows.length} onClick={exportCsv}>Export CSV</Button>}>
      <div className="fa-transaction-controls">
        <Field label="Search transactions" placeholder="Description, account, date, or amount" value={search} onChange={(event) => { setSearch(event.target.value); setPage(0); }} />
        <Select label="Spending category" value={category || ""} onChange={(event) => chooseCategory(event.target.value || null)}>
          <option value="">All categories</option>
          {data.categoryOptions.map((item) => <option key={item.key} value={item.key}>{item.label}</option>)}
        </Select>
      </div>
      <Tabs label="Transaction types" value={kind} onChange={(id) => { setKind(id); setCategory(null); setPage(0); }} items={[
        { id: "all", label: "All activity" }, { id: "expense", label: "Spending" }, { id: "income", label: "Income" },
        { id: "transfer", label: "Transfers" }, { id: "debt-payment", label: "Debt payments" },
      ]} panelId={tableId} />
      {category && <div className="fa-selected"><span>Showing {data.categoryOptions.find((item) => item.key === category)?.label || category}</span><Button size="compact" variant="quiet" icon="fa-solid fa-xmark" onClick={() => chooseCategory(null)}>Clear category</Button></div>}
      <div id={tableId} role="tabpanel" aria-label={kind === "all" ? "All activity" : KIND_LABELS[kind as keyof typeof KIND_LABELS]} className="fa-transactions-panel">
        <table className="fa-transactions-table">
          <caption className="ds-sr-only">{period} transactions, {account ? accountOptions.find(([id]) => id === account)?.[1] || account : "all accounts"}</caption>
          <thead><tr><th scope="col">Date</th><th scope="col">Transaction</th><th scope="col">Category / type</th><th scope="col">Account</th><th scope="col" className="fa-amount">Amount</th></tr></thead>
          <tbody>{visibleRows.map((row) => <tr key={row.id}>
            <td data-label="Date">{row.iso || row.date || "Date not recorded"}</td>
            <td data-label="Transaction" className="fa-transaction-label">{row.label}</td>
            <td data-label="Category / type"><span className="fa-kind">{row.categoryLabel || KIND_LABELS[row.kind]}</span></td>
            <td data-label="Account">{transactionAccount(row)}</td>
            <td data-label="Amount" className={`fa-amount${row.kind === "income" ? " fa-amount--income" : ""}`}>{row.kind === "income" ? "+" : row.kind === "transfer" ? "" : "−"}{money(row.amount)}</td>
          </tr>)}</tbody>
        </table>
        {!rows.length && <div className="fa-empty"><i className="fa-solid fa-receipt" aria-hidden="true" /><h3>{data.allTransactions.length ? "No matching transactions" : "Your transactions will appear here"}</h3><p>{data.allTransactions.length ? "Try another period, account, or category, or clear your search." : "Log an expense or income in Money in & out to start seeing your finances."}</p></div>}
      </div>
      <div className="fa-pagination">
        <p role="status" aria-live="polite" aria-atomic="true">{rows.length ? `${safePage * PAGE_SIZE + 1}–${Math.min((safePage + 1) * PAGE_SIZE, rows.length)} of ${rows.length} transactions` : "0 transactions"}</p>
        <div><Button size="compact" aria-label="Previous transactions page" aria-controls={tableId} disabled={safePage === 0} onClick={() => setPage(safePage - 1)}>Previous</Button><Button size="compact" aria-label="Next transactions page" aria-controls={tableId} disabled={safePage >= pages - 1} onClick={() => setPage(safePage + 1)}>Next</Button></div>
      </div>
      <p className="fa-note">Graphs show recorded activity. Recurring bills awaiting payment are in Accounts &amp; bills. Dates without a year are shown in {nowIso.slice(0, 4)}. Entries without a usable date appear under All recorded months.</p>
    </Card>
    {(data.totalDebt > 0 || data.totalReceivable > 0) && <Card title="Outstanding debt now" description={`${money(data.totalDebt)} across recorded debt balances. Money owed to you is shown separately.`}>
      <BalanceBars items={data.debts.map((item) => ({ id: item.id, label: item.name, amount: item.balance }))} label="Outstanding debt by lender" note="Bar lengths compare each debt with the largest recorded balance." />
      {data.totalReceivable > 0 && <p className="fa-note">Money owed to you: <strong>{money(data.totalReceivable)}</strong>.</p>}
    </Card>}
  </div>;
}
